import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Heading, Text } from "@radix-ui/themes"
import { z } from "zod"

import FileUpload from "@/components/file-upload"
import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { projectCustomersPath, projectDashboardPath, projectImportsPath } from "@/routes"
import { ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
})

type Props = z.infer<typeof PropsSchema>

export default function NewImport(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project } = validatedProps

  const { data, setData, post, processing, errors } = useForm({
    csv_file: null as File | null,
  })

  const handleFileSelect = (file: File | null) => {
    setData("csv_file", file)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(projectImportsPath(project.slug))
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Customers", href: projectCustomersPath(project.slug) },
        { title: "Import customers" },
      ]}
    >
      <Head title="Import customers" />

      <Flex direction="column" gap="6" p="6">
        <Flex direction="column" gap="1">
          <Heading size="6" weight="medium">
            Import customers
          </Heading>
          <Text color="gray" size="2">
            Upload a CSV file to import customers to your project
          </Text>
        </Flex>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <FileUpload
              file={data.csv_file}
              onFileSelect={handleFileSelect}
              accept=".csv"
              label="CSV file"
              description="Only CSV files are supported"
              error={errors.csv_file}
              disabled={processing}
              required
            />

            <Flex gap="3" justify="end" mt="4">
              <LinkButton href={projectCustomersPath(project.slug)} variant="soft" color="gray">
                Cancel
              </LinkButton>

              <Button type="submit" disabled={!data.csv_file || processing}>
                {processing ? "Uploading..." : "Upload CSV"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </ProjectLayout>
  )
}
